// The birthday a to-do to congratulate is for (docs/59, 5), as the kit has it (Birthdays.swift:
// `Birthday.congratulationID(year:)`, `BirthdayDay.congratulated(by:in:)`). The apps make that to-do with an id worked
// out from the birthday's id and the year, the same on every device; nothing else links the two, so the birthday is
// found by working the id out again. Cases: cases/birthdays.json.

import { dayParts } from "./days";
import { birthdayIn, type BirthdayDay, type Lists } from "./lists";
import type { Birthday } from "./records";
import type { Day } from "./types";

/** The id of the to-do to congratulate on a birthday in a year, in upper case as Swift writes it (UUID.stable). */
export function congratulationID(birthdayID: string, year: number): string {
  return stableUUID(`congratulate:${birthdayID.toUpperCase()}:${year}`);
}

/**
 * The birthday a to-do congratulates on, with the day it fell on, or null for any other to-do. This year (of `today`)
 * and last year are tried, as a to-do made on 31 December may still be open in January. Pass the shown birthdays
 * (`Lists.shownBirthdays`): a hidden one is out of sight.
 */
export function congratulatedBy(todoID: string, birthdays: readonly Birthday[], today: Day): BirthdayDay | null {
  const id = todoID.toUpperCase();
  const thisYear = dayParts(today).year;
  for (const year of [thisYear, thisYear - 1]) {
    for (const birthday of birthdays) {
      if (congratulationID(birthday.id, year) !== id) continue;
      const day = birthdayIn(birthday, year);
      if (day !== null) return { birthday, day };
    }
  }
  return null;
}

/** The to-dos to congratulate of each `Lists`, by id: worked out once per `Lists` (a new one comes with each change). */
const congratulations = new WeakMap<Lists, Map<string, BirthdayDay>>();

/**
 * `congratulatedBy` over the shown birthdays of `lists` on its day, for rows that ask as they draw: the ids are worked
 * out once per `Lists`, as `Store.birthday(congratulatedBy:)` keeps them, so a row costs a lookup.
 */
export function congratulatedIn(lists: Lists, todoID: string): BirthdayDay | null {
  let ids = congratulations.get(lists);
  if (ids === undefined) {
    ids = new Map();
    const thisYear = dayParts(lists.today).year;
    // This year's first, as `congratulatedBy` tries it first.
    for (const year of [thisYear, thisYear - 1]) {
      for (const birthday of lists.shownBirthdays) {
        const day = birthdayIn(birthday, year);
        if (day === null) continue;
        const id = congratulationID(birthday.id, year);
        if (!ids.has(id)) ids.set(id, { birthday, day });
      }
    }
    congratulations.set(lists, ids);
  }
  return ids.get(todoID.toUpperCase()) ?? null;
}

/** The age reached on the birthday's day, when the year of birth is known (Birthday.age(on:)). */
export function ageOn(birthday: Birthday, day: Day): number | undefined {
  return birthday.year === undefined ? undefined : dayParts(day).year - birthday.year;
}

// MARK: - UUID.stable

/** A UUID that always comes out the same for the same text: the first 16 bytes of its SHA-256, as a version 5 UUID. */
export function stableUUID(text: string): string {
  const bytes = [...sha256(new TextEncoder().encode(text)).slice(0, 16)];
  bytes[6] = (at(bytes, 6) & 0x0f) | 0x50;
  bytes[8] = (at(bytes, 8) & 0x3f) | 0x80;
  const hex = bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** SHA-256, synchronous (the browser's crypto.subtle only answers later). For short texts such as ids. */
export function sha256(data: Uint8Array): Uint8Array {
  const length = data.length;
  const padded = new Uint8Array(Math.ceil((length + 9) / 64) * 64);
  padded.set(data);
  padded[length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(length / 0x20000000));
  view.setUint32(padded.length - 4, (length * 8) >>> 0);
  const hash = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const w = new Array<number>(64).fill(0);
  const rotate = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let block = 0; block < padded.length; block += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(block + i * 4);
    for (let i = 16; i < 64; i++) {
      const w15 = at(w, i - 15);
      const w2 = at(w, i - 2);
      const s0 = rotate(w15, 7) ^ rotate(w15, 18) ^ (w15 >>> 3);
      const s1 = rotate(w2, 17) ^ rotate(w2, 19) ^ (w2 >>> 10);
      w[i] = (at(w, i - 16) + s0 + at(w, i - 7) + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = hash.map((_, i) => at(hash, i)) as [number, number, number, number, number, number, number, number];
    for (let i = 0; i < 64; i++) {
      const t1 = (h + (rotate(e, 6) ^ rotate(e, 11) ^ rotate(e, 25)) + ((e & f) ^ (~e & g)) + at(K, i) + at(w, i)) >>> 0;
      const t2 = ((rotate(a, 2) ^ rotate(a, 13) ^ rotate(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, h].forEach((word, i) => { hash[i] = (at(hash, i) + word) >>> 0; });
  }
  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  hash.forEach((word, i) => outView.setUint32(i * 4, word));
  return out;
}

/** An element known to be there (the hash's arrays have fixed lengths). */
function at(array: ArrayLike<number>, index: number): number {
  return array[index] ?? 0;
}
